import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { RemoveDuplicateArrayByKey } from "../../lib/utils";
import { useSocket } from "../../hooks/useSocket";
import { IMessage, TypeMessage } from "../../types/message";
import MessageInput from "./MessageInput";
import { IGroup } from "../../types/group";
import { getMessages } from "../../api/messages/messageService";

const PER_PAGE = 50;

interface MessageBoxProps {
  selectedGroup: IGroup;
}

// Minimal optimistic-status fields (no DB impact)
type LocalMessage = IMessage & {
  clientId?: string;
  sendStatus?: "sending" | "sent" | "failed";
  sendError?: string;
};

const MessageBox: React.FC<MessageBoxProps> = ({ selectedGroup }) => {
  const token = (localStorage.getItem("accessToken") || "").toString();
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const group = JSON.parse(localStorage.getItem("directMessage") || "{}");
  const inputRef = useRef<HTMLInputElement>(null);
  // Store history of messages locally
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [filter, setFilter] = useState<any>({});
  const [toIndex, setToIndex] = useState<number | undefined>();

  const endMsg = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [oldMessages, setOldMessages] = useState<IMessage[]>([]);
  const [loadingMessage, setLoadingMessage] = useState<boolean>(false);
  const isInitialLoad = useRef<boolean>(true);

  useEffect(() => {
    setLocalMessages([]);
    isInitialLoad.current = true;
    setToIndex(selectedGroup.lastMsgIndex);
  }, [selectedGroup]);

  useEffect(() => {
    setFilter({
      toIndex: toIndex,
      limit: PER_PAGE,
    });
  }, [toIndex]);

  const handleGetMessages = async () => {
    try {
      setLoadingMessage(true);
      const result = await getMessages(selectedGroup.id, filter);
      setOldMessages(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMessage(false);
    }
  };

  useEffect(() => {
    if (filter.toIndex !== undefined && filter.limit !== undefined) {
      // console.log("haha", filter);
      handleGetMessages();
    }
  }, [filter]);

  const appendMessage = useCallback(
    (newMessage: IMessage) => {
      const updated = {
        ...JSON.parse(localStorage.getItem("directMessage") || "{}"),
        lastMsgIndex: newMessage.index,
        lastMessage: {
          content: newMessage.content,
        },
      };
      localStorage.setItem("directMessage", JSON.stringify(updated));
      // Update groups in localStorage
      const groups = JSON.parse(localStorage.getItem("groups") || "[]");
      const updatedGroups = groups.map((g: IGroup) => {
        if (g.id === newMessage.groupId) {
          return {
            ...g,
            lastMsgIndex: newMessage.index,
            lastMessage: {
              content: newMessage.content,
            },
          };
        }
        return g;
      });
      localStorage.setItem("groups", JSON.stringify(updatedGroups));

      setLocalMessages((prev) => {
        // already have this server message
        if (prev.some((m) => m.id === newMessage.id)) return prev;

        // If it's our own message, try to reconcile with an optimistic "sending" one
        if (newMessage.senderId === profile.id) {
          const idx = prev.findIndex(
            (m) =>
              m.senderId === profile.id &&
              m.sendStatus === "sending" &&
              (m.type || "").toLowerCase() ===
                (newMessage.type || "").toLowerCase() &&
              m.content === newMessage.content
          );
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...(newMessage as any), sendStatus: "sent" };
            return next;
          }
        }

        return [...prev, newMessage as any];
      });
    },
    [setLocalMessages, profile.id]
  );

  useEffect(() => {
    if (oldMessages && oldMessages.length > 0) {
      const newData = [...oldMessages].sort((a, b) => a.index - b.index);
      if (newData[newData.length - 1].index >= group.lastMsgIndex) {
        setTimeout(() => {
          scrollToMsgIndex();
          isInitialLoad.current = false;
        }, 500);
      } else {
        setTimeout(() => {
          scrollToMsgIndex(10);
          isInitialLoad.current = false;
        }, 500);
      }
      setLocalMessages((prev) => {
        const newList = RemoveDuplicateArrayByKey([...newData, ...prev], "id");
        return newList;
      });
    }
  }, [oldMessages]);

  const { isConnected, joinRoom, sendMessage } = useSocket({
    token,
    onNewMessage: (message: any) => {
      appendMessage(message);

      // Scroll to the bottom after receiving a new message
      setTimeout(() => {
        scrollToMsgIndex();
      }, 500);
    },
  });

  useEffect(() => {
    if (isConnected) {
      joinRoom(group.id);
    }
  }, [isConnected, group.id, joinRoom]);

  const scrollToMsgIndex = (index?: number) => {
    const container = document.querySelector(".mesages-container");
    // If no index is provided, scroll to the last message
    if (!index && index !== 0) {
      if (container && container.lastElementChild) {
        (container.lastElementChild as HTMLElement).scrollIntoView({
          behavior: "smooth",
        });
      }
    }
    // If an index is provided, scroll to that specific message
    else {
      // find the messge position with the given index in container
      const messageElement = Array.from(container?.children || []);
      if (messageElement.length > index) {
        messageElement[10].scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const makeClientId = () =>
    `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const handleSend = async (message: string, type: string) => {
    const lowcaseType = type.toLowerCase() as TypeMessage;
    const trimmed = message.trim();
    if (trimmed) {
      const payload = {
        groupId: group.id,
        content: trimmed,
        type: lowcaseType,
      };
      const clientId = makeClientId();

      // Optimistic message (but now tracked as sending/failed)
      setLocalMessages((prev) => {
        const lastIndex = prev.length ? (prev[prev.length - 1].index ?? 0) : 0;
        return [
          ...prev,
          {
            id: clientId, // temp id until saved
            clientId,
            groupId: group.id,
            content: trimmed,
            type: lowcaseType,
            senderId: profile.id,
            index: lastIndex + 1,
            createdAt: new Date(),
            updatedAt: new Date(),
            sendStatus: "sending",
          } as LocalMessage,
        ];
      });

      try {
        const saved = await sendMessage(payload);
        // Replace optimistic with saved message (real DB id/index)
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.clientId === clientId
              ? ({ ...(saved as any), sendStatus: "sent" } as LocalMessage)
              : m
          )
        );
      } catch (e: any) {
        const errMsg = e?.message || "Failed to send";
        // Mark failed (no more ghost “sent” messages)
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.clientId === clientId
              ? ({
                  ...m,
                  sendStatus: "failed",
                  sendError: errMsg,
                } as LocalMessage)
              : m
          )
        );
        throw e;
      }

      if (inputRef.current) {
        inputRef.current.value = "";
      }
      scrollToMsgIndex();
    }
  };

  const retrySend = useCallback(
    async (msg: LocalMessage) => {
      if (!msg.clientId || msg.sendStatus !== "failed") return;
      const payload = {
        groupId: msg.groupId,
        content: msg.content,
        type:
          (msg.type?.toLowerCase() as TypeMessage) || ("txt" as TypeMessage),
      };

      setLocalMessages((prev) =>
        prev.map((m) =>
          m.clientId === msg.clientId
            ? ({ ...m, sendStatus: "sending", sendError: "" } as LocalMessage)
            : m
        )
      );

      try {
        const saved = await sendMessage(payload);
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.clientId === msg.clientId
              ? ({ ...(saved as any), sendStatus: "sent" } as LocalMessage)
              : m
          )
        );
      } catch (e: any) {
        const errMsg = e?.message || "Failed to send";
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.clientId === msg.clientId
              ? ({
                  ...m,
                  sendStatus: "failed",
                  sendError: errMsg,
                } as LocalMessage)
              : m
          )
        );
      }
    },
    [sendMessage]
  );

  const handleScroll = useCallback(() => {
    // Don't trigger scroll handling during initial load
    if (isInitialLoad.current) return;

    if (!loadingMessage) {
      const container = scrollRef.current;
      if (!container || filter.toIndex <= 0) return;

      if (container.scrollTop <= 0) {
        // console.log("scroll new index");
        const newToindex = localMessages[0]?.index || 0;
        setToIndex(newToindex);
      }
    }
  }, [localMessages]);

  return (
    <div className="flex flex-col justify-between h-[calc(100vh-100px)] pb-20 lg:pb-4">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="mesages-container flex-1 max-h-full overflow-y-auto px-4 py-2 space-y-4"
      >
        {localMessages.map((msg) => {
          const isCurrentUser = msg.senderId === profile.id;
          const type = msg.type?.toLowerCase();
          // Format time
          let timeString = "";
          if (msg.createdAt) {
            const date = new Date(msg.createdAt);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            timeString = `${day}/${month}/${year}, ${hours}:${minutes}`;
          }
          // Get sender name
          let senderName = "";
          if (isCurrentUser) {
            senderName = "You";
          } else if (
            msg.sender &&
            (msg.sender.firstName || msg.sender.lastName)
          ) {
            senderName =
              `${msg.sender.firstName || ""} ${msg.sender.lastName || ""}`.trim();
          } else if (selectedGroup?.members) {
            const found = selectedGroup.members.find(
              (m) => m.id === msg.senderId
            );
            if (found)
              senderName =
                `${found.firstName || ""} ${found.lastName || ""}`.trim();
          }
          const renderContent = () => {
            switch (type) {
              case "txt":
                return (
                  <div>
                    {msg.content.split("\n").map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < msg.content.split("\n").length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                );
              case "image":
                return (
                  <img
                    src={msg.content}
                    alt="image-message"
                    className="max-w-[200px] rounded-md"
                  />
                );
              case "audio":
                return <audio src={msg.content} controls></audio>;
              case "video":
                return (
                  <video
                    controls
                    className="max-w-[200px] max-h-[200px] md:max-h-[400px] rounded-md"
                  >
                    <source src={msg.content} type="video/mp4" />
                  </video>
                );
              default:
                return <span>{msg.content}</span>;
            }
          };
          const isTextFromCurrentUser = type === "txt" && isCurrentUser;
          return (
            <div
              key={msg.id}
              className={clsx(
                "flex flex-col text-sm",
                isCurrentUser ? "ml-auto items-end" : "items-start"
              )}
            >
              <div
                className={clsx(
                  "rounded-xl px-4 py-2 shadow-sm",
                  isTextFromCurrentUser
                    ? "bg-orange-100 text-gray-800"
                    : "bg-gray-100 text-gray-700"
                )}
              >
                {/* Sender name and time */}
                <div className={clsx("flex items-center gap-2 mb-1")}>
                  <span
                    className={clsx(
                      "text-xs font-semibold",
                      isCurrentUser ? "text-orange-500" : "text-blue-600"
                    )}
                  >
                    {senderName}
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {timeString}
                  </span>
                  {isCurrentUser &&
                    (msg as LocalMessage).sendStatus === "sending" && (
                      <span className="text-xs text-gray-400">• Sending</span>
                    )}
                  {isCurrentUser &&
                    (msg as LocalMessage).sendStatus === "failed" && (
                      <span className="text-xs text-red-500">• Failed</span>
                    )}
                </div>
                <div style={{ overflowWrap: "anywhere" }}>
                  {renderContent()}
                </div>
                {isCurrentUser &&
                  (msg as LocalMessage).sendStatus === "failed" && (
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => retrySend(msg as LocalMessage)}
                        className="text-xs text-orange-600 hover:underline"
                      >
                        Retry
                      </button>
                      {(msg as LocalMessage).sendError ? (
                        <span
                          className="text-xs text-gray-400"
                          title={(msg as LocalMessage).sendError}
                        >
                          {(msg as LocalMessage).sendError}
                        </span>
                      ) : null}
                    </div>
                  )}
              </div>
            </div>
          );
        })}
        <div ref={endMsg} />
      </div>

      <MessageInput onSendMessage={handleSend} />
    </div>
  );
};

export default MessageBox;
